// Meeting-related type definitions

export enum AttendeePriority {
  Critical = 'critical',
  Required = 'required',
  Optional = 'optional'
}

export type RepeatOption = 'none' | 'daily' | 'weekly' | 'monthly';

export interface MeetingFormData {
  subject: string;
  description: string;
  agenda: string;
  duration: number;
  repeat: RepeatOption;
  onBehalfOfLogin: string | null;
  organizerAlias: string;
}

// Default values for MeetingFormData
export const DEFAULT_MEETING_FORM_DATA: MeetingFormData = {
  subject: '',
  description: '',
  agenda: '',
  duration: 30,
  repeat: 'none',
  onBehalfOfLogin: null,
  organizerAlias: ''
};

export interface ResolvedMatch {
  inputName: string;
  matches: any[];
  selectedEmployee?: any;
  status: 'exact' | 'fuzzy' | 'multiple' | 'notfound' | 'manual';
  priority: AttendeePriority;
  id: string;
  resolvedEmail: string;
  editedName?: string;
  selectedExecutiveAssistant?: any;
  hasError?: boolean;
  errorMessage?: string;
  manualEmail?: string;
  isFavorite?: boolean;
  /** Login of the attendee this person is proxying for */
  proxyForLogin?: string;
  /** Display name of the attendee this person is proxying for */
  proxyForName?: string;
}

export interface DocumentParseResult {
  success: boolean;
  data?: any;
  htmlContent?: string;
  error?: string;
}

export interface Employee {
  login_name: string;
  employee_name: string;
  employee_internal_email_address: string;
  job_title_name: string;
  location_city_name: string;
  location_state_name: string;
  job_level_name: string;
  employee_assistant_employee_id?: string;
  emplid?: string;
  displayName?: string;
  department_name_current: string;
  badgeColor?: string;
  timezone?: string;
  timeZoneId?: string;
}

/**
 * Attendee entry stored as part of a saved meeting.
 */
export interface MeetingAttendee {
  loginName: string;
  priority: AttendeePriority;
  /** EA login resolved at add-time, used for access control */
  eaLogin?: string;
  /** Employee display name, stored for display without PAPI lookup */
  employeeName?: string;
  /** Login of the attendee this person is proxying for */
  proxyForLogin?: string;
  /** Display name of the attendee this person is proxying for */
  proxyForName?: string;
}

/**
 * Full meeting data persisted to DynamoDB when a meeting is created.
 * Keyed by creator alias (pk) and MEETING#<meetingId> (sk).
 */
export interface MeetingData {
  meetingId: string;
  subject: string;
  agenda?: string;
  createdBy: string;
  createdAt: string;
  attendees: MeetingAttendee[];
  organizerLogin: string;
  duration: number;
  repeat: RepeatOption;
  onBehalfOfLogin?: string | null;
  attachments?: string[];
  /** Denormalized set of logins authorized to access this meeting (creator + attendees + EAs) */
  authorizedLogins?: string[];
  /** ID of the selected/finalized time slot */
  selectedSlotId?: string | null;
}

// ── Time Slot Scheduling Types ─────────────────────────────────────────

export interface TimeSlotVote {
  loginName: string;
  vote: 'up' | 'down';
  votedAt: string;
  // TODO: Add leader login
}

/**
 * Meeting-level comment with optional reply threading.
 * Stored under sk=MEETING#<meetingId>#COMMENTS as an array.
 */
export interface MeetingComment {
  commentId: string;
  parentCommentId?: string; // undefined = top-level comment, set = reply to another comment
  loginName: string;
  employeeName: string;
  content: string;
  createdAt: string;
  updatedAt?: string;
}

export interface TimeSlot {
  slotId: string;
  date: string; // YYYY-MM-DD
  startTime: string; // HH:mm
  endTime: string; // HH:mm
  timezone: string; // IANA timezone e.g., "America/Los_Angeles"
  order: number; // for drag-reorder
  votes: TimeSlotVote[];
  isDeleted?: boolean; // soft-delete flag
}

export interface SchedulingData {
  meetingId: string;
  organizerLogin: string;
  duration: number; // minutes (15, 30, 45, 60, or custom up to 86400)
  repeat: RepeatOption;
  timeSlots: TimeSlot[];
}

/**
 * Summary record shown in the "Recent Meetings" panel.
 * Stored under the creator's RECENT_MEETINGS sort key.
 */
export interface RecentMeeting {
  meetingId: string;
  tinyUrl: string;
  subject: string;
  /** Full attendee list with priority — used for popover display in Recent Meetings */
  attendees: MeetingAttendee[];
  createdBy: string;
  createdAt: string;
  /** Soft-delete flag — when true, the meeting is hidden from the UI */
  isDeleted?: boolean;
}

// ── Grouped prop interfaces for AddAttendeesContainer ──────────────────

/** Attendee CRUD callbacks and data for the attendees list */
export interface AttendeeListProps {
  resolvedAttendees: ResolvedMatch[];
  existingEmails: Set<string>;
  duplicateEmails: Set<string>;
  onAddAttendee: (employee: Employee, priority: AttendeePriority, extraFields?: { proxyForLogin?: string; proxyForName?: string }) => void;
  onAttendeeUpdate: (id: string, updates: Partial<ResolvedMatch>) => void;
  onAttendeeRemove: (id: string) => void;
  onToggleFavorite: (id: string) => void;
  onAddProxy: (principalId: string, proxyEmployee: Employee) => void;
  onResort: () => void;
}

/** Document upload and processing state */
export interface DocumentUploadProps {
  isProcessingDocument: boolean;
  documentError: string;
  onFileSelected: (file: File) => void;
  onFormattedContentExtracted: (htmlContent: string) => void;
  showUploadModal: boolean;
  onHideUploadModal: () => void;
}

/** Shared meeting viewer state (loading, read-only, save) */
export interface SharedMeetingViewProps {
  isLoadingSharedMeeting: boolean;
  sharedMeetingProgress: string;
  isReadOnly: boolean;
  onSaveMeeting?: () => void;
  isSaving?: boolean;
}

// ── Notifications ────────────────────────────────────────────────────

export type NotificationType = 'vote' | 'comment' | 'reply';

export interface MeetingNotification {
  id: string;
  type: NotificationType;
  actorLogin: string;
  actorName: string;
  meetingId: string;
  meetingSubject: string;
  /** Alias of the meeting creator — used to build the meeting URL. */
  meetingCreator?: string;
  slotId?: string;
  slotLabel?: string;
  /** Vote direction, only for type=vote */
  voteDirection?: 'up' | 'down';
  /** Preview of comment content, only for type=comment|reply */
  commentPreview?: string;
  createdAt: string;
  isRead: boolean;
  isMarkRead: boolean;
}
